<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import ClipLoader from '@/components/ui/ClipLoader.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import Alert from '@/components/Atoms/Alert.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconCopy from '@/components/icon/IconCopy.vue'
import IconEye from '@/components/icon/IconEye.vue'
import IconEyeCrossed from '@/components/icon/IconEyeCrossed.vue'
import IconArrowRight from '@/components/icon/IconArrowRight.vue'
import IconCheckmark from '@/components/icon/IconCheckmark.vue'
import { isUnreachableStatus, windowOf, formatCountdown, durationLabel, formatClock } from './captionLanguages'
import {
	DEFAULT_LIMITS,
	DEFAULT_USAGE,
	UPGRADE_ROUTE,
	hasPool,
	poolExhausted,
	concurrencyBlocked,
	poolExhaustedTooltip,
	concurrencyTooltip,
	durationPresetsFor,
	formatHours,
	hoursRunOutLabel,
	billedByProvider,
} from './captionLimits'
import {
	FALLBACK_ENGINE_INFO,
	PROVIDER_ROUTE,
	PROVIDER_FLOW_CODES,
	syncwordsHeadline,
	syncwordsLanguageName,
	syncwordsEndedCopy,
	autoStopLabel,
	flowLanguages,
	teletextPage,
	isOwnAccountFlow,
	providerUsageLabel,
} from './captionSyncWords'
import { track, EVENTS } from '@/lib/tracking'

/**
 * "Go live with captions" for a SyncWords flow, in the order it has to
 * happen: 1. start captions (the window and its duration, Stop while
 * running), 2. send the encoder to the SRT ingest, which only accepts it
 * once the service runs (Starting takes a minute or two, then Waiting for
 * input). The step that needs the user is highlighted.
 */
const props = defineProps({
	flow: { type: Object, required: true },
	status: { type: Object, default: null },
	info: { type: Object, default: () => FALLBACK_ENGINE_INFO },
	skewMs: { type: Number, default: 0 },
	// Seconds left (the window's, or the plan's hours when those end first); null when unlimited.
	remainingSec: { type: Number, default: null },
	poolEndsFirst: { type: Boolean, default: false },
	error: { type: String, default: '' },
	unreachable: { type: Boolean, default: false },
	limits: { type: Object, default: () => ({ ...DEFAULT_LIMITS }) },
	usage: { type: Object, default: () => ({ ...DEFAULT_USAGE }) },
	starting: { type: Boolean, default: false },
	stopping: { type: Boolean, default: false },
	// The settings card's draft: { dirty, valid }. Unsaved settings go with Start.
	pending: { type: Object, default: () => ({ dirty: false, valid: true }) },
	// { status, code, message, action, upgrade? } from the last failed save/start, or null.
	serverError: { type: Object, default: null },
	// Just created: put focus on Start.
	focusStart: { type: Boolean, default: false },
	// Own-account flows: the linked account's usage this month (`normalizeProviderUsage`), or null.
	providerUsage: { type: Object, default: null },
})

const emit = defineEmits(['start', 'stop'])

const { notify } = useNotification()

const now = ref(Date.now())
let tick = null
onMounted(() => { tick = setInterval(() => { now.value = Date.now() }, 1000) })
onBeforeUnmount(() => clearInterval(tick))

const statusUsable = computed(() => !!props.status && !isUnreachableStatus(props.status))
const win = computed(() => windowOf(statusUsable.value ? props.status : null, props.flow))
const running = computed(() => win.value.state === 'running')
const headline = computed(() => (props.unreachable ? { key: 'unknown', label: 'Unknown', tone: 'warning' } : syncwordsHeadline(props.flow, props.status)))
const key = computed(() => headline.value.key)
const isStarting = computed(() => key.value === 'starting')
const isWaiting = computed(() => key.value === 'waiting-input')
const isLive = computed(() => key.value === 'live')
const langs = computed(() => flowLanguages(props.flow))
const langNames = computed(() => langs.value.map((code) => syncwordsLanguageName(code, props.info)))
// "English · Live" for one language, "3 languages · Live" for more; the row below names them.
const pillText = computed(() => {
	if (!langs.value.length) return headline.value.label
	const subject = langs.value.length === 1 ? langNames.value[0] : `${langs.value.length} languages`
	return `${subject} · ${headline.value.label}`
})
const streamName = computed(() => props.flow.streamName || 'your Castr stream')
const streamRoute = computed(() => (props.flow.streamId ? { name: 'LivestreamManage', params: { streamId: props.flow.streamId } } : null))
const captionSupport = computed(() => props.flow.syncwords?.captionSupport || null)
const playerSettingsRoute = computed(() => (props.flow.streamId ? { name: 'StreamsManageSettingsPlayerFunctions', params: { streamId: props.flow.streamId } } : null))

// The step that needs the user: Start while off, the encoder once the ingest is open.
const activeStep = computed(() => {
	if (!running.value) return 1
	if (isStarting.value) return 1
	return 2
})

const pillClass = computed(() => {
	switch (headline.value.tone) {
		case 'success':
			return 'bg-success-transparent text-success ring-success/30'
		case 'warning':
			return 'bg-surface-3 text-gold ring-gold/30'
		case 'error':
			return 'bg-error-transparent text-error-light ring-error/30'
		default:
			return 'bg-surface-3 text-surface-8 ring-surface-4'
	}
})
const dotClass = computed(() => {
	switch (headline.value.tone) {
		case 'success':
			return 'bg-success animate-pulse'
		case 'warning':
			return 'bg-gold'
		case 'error':
			return 'bg-error'
		default:
			return 'bg-surface-6'
	}
})

// ------------------------------------------------------------ Running window
const countdownLabel = computed(() => {
	if (!running.value) return ''
	if (props.poolEndsFirst && Number.isFinite(props.remainingSec)) {
		return props.remainingSec <= 0 ? 'Hours used up' : hoursRunOutLabel(props.remainingSec)
	}
	if (win.value.durationSec === null || win.value.durationSec === undefined) return 'Unlimited'
	if (!Number.isFinite(props.remainingSec)) return ''
	return props.remainingSec <= 0 ? 'Ended' : `Ends in ${formatCountdown(props.remainingSec)}`
})
const unlimited = computed(() => win.value.durationSec === null || win.value.durationSec === undefined)
const endsAtLabel = computed(() => (Number.isFinite(win.value.expiresAt) ? formatClock(win.value.expiresAt) : ''))
const autoStop = computed(() => autoStopLabel(statusUsable.value ? props.status : null, props.info?.syncwords?.disconnectStopMin))
const inputConnected = computed(() => (statusUsable.value ? props.status.syncwords?.isInputConnected ?? null : null))
const offCopy = computed(() => (win.value.state === 'idle' ? '' : syncwordsEndedCopy(win.value)))

// ------------------------------------------------------------ Duration presets
const durationSec = ref(3600)
// On the customer's own account Castr's plan neither bills nor limits the flow: no usage shown.
const ownAccount = computed(() => isOwnAccountFlow(props.flow))
const ownBilling = computed(() => ownAccount.value || billedByProvider(props.limits))
const planActive = computed(() => !ownBilling.value && props.limits?.enforced === true)
const poolFinite = computed(() => !ownBilling.value && hasPool(props.limits))
const poolRemaining = computed(() => (poolFinite.value && Number.isFinite(props.usage?.remainingSec) ? props.usage.remainingSec : null))
const exhausted = computed(() => !ownBilling.value && poolExhausted(props.limits, props.usage))
const showBetaBadge = computed(() => !ownBilling.value && !poolFinite.value && !planActive.value)
const cycleLeftLabel = computed(() => (poolRemaining.value === null ? '' : `${formatHours(poolRemaining.value)} left this cycle`))
// In place of the plan's hours: what the customer's own account captioned this month.
const usageLabel = computed(() => (ownBilling.value ? providerUsageLabel(props.providerUsage) : ''))
const durationPresets = computed(() => durationPresetsFor(ownBilling.value ? null : props.limits, poolRemaining.value))
const restOfHours = computed(() => durationPresets.value.some((p) => p.value === null) && poolFinite.value && planActive.value)

watch(durationPresets, (list) => {
	if (list.some((p) => p.value === durationSec.value)) return
	const numeric = list.filter((p) => p.value !== null)
	if (numeric.some((p) => p.value === 3600)) durationSec.value = 3600
	else if (numeric.length) durationSec.value = numeric[numeric.length - 1].value
	else durationSec.value = list.length ? list[0].value : null
}, { immediate: true })

const stopMin = computed(() => props.info?.syncwords?.disconnectStopMin)
const durationCopy = computed(() => {
	if (!durationPresets.value.length) return 'No captioning hours are left this cycle. Upgrade, or wait for the reset, to start a new window.'
	const inputLoss = Number.isFinite(stopMin.value) ? `, or ${stopMin.value} min after your encoder stops sending` : ''
	if (durationSec.value === null) {
		if (restOfHours.value) return `Runs until you stop it, your ${formatHours(poolRemaining.value)} left run out${inputLoss}.`
		return `Runs until you stop it${inputLoss}.`
	}
	return 'Counts down from Start, even while your encoder is offline.'
})

// ------------------------------------------------------------------- Start
const concurrencyHit = computed(() => !ownBilling.value && !running.value && concurrencyBlocked(props.limits, props.usage))
const startBlockedCode = computed(() => {
	if (exhausted.value) return 'captions_pool_exhausted'
	if (concurrencyHit.value) return 'captions_concurrency_limit'
	return ''
})
const startBlockedReason = computed(() => {
	if (exhausted.value) return poolExhaustedTooltip(props.limits)
	if (concurrencyHit.value) return concurrencyTooltip(props.limits)
	return ''
})
const settingsValid = computed(() => props.pending?.valid !== false)
const canStart = computed(() => settingsValid.value && !props.starting && !startBlockedReason.value)
const startTooltip = computed(() => {
	if (startBlockedReason.value) return startBlockedReason.value
	if (!settingsValid.value) return 'Fix the caption settings to start.'
	return ''
})
const ended = computed(() => win.value.state === 'stopped' || win.value.state === 'expired')

const noted = new Set()
function noteStartBlocked(action) {
	if (!startBlockedCode.value) return
	const code = `${startBlockedCode.value}:${action}`
	if (noted.has(code)) return
	noted.add(code)
	track(EVENTS.CAPTIONING_CAP_HIT, { code: startBlockedCode.value, action, surface: 'manage' })
}

function start() {
	if (startBlockedReason.value) {
		noteStartBlocked('click')
		return
	}
	if (!canStart.value) return
	emit('start', { durationSec: durationSec.value })
}

const startButton = ref(null)
onMounted(async () => {
	if (!props.focusStart) return
	await nextTick()
	const el = startButton.value?.$el ?? startButton.value
	el?.scrollIntoView?.({ block: 'center' })
	el?.focus?.()
})

// Start failures; field errors of unsaved settings show on the settings card.
const FIELD_CODES = ['captions_invalid_settings', 'captions_too_many_languages', 'captions_unsupported_caption_language']
const startError = computed(() => (props.serverError?.action === 'start' ? props.serverError : null))
const providerError = computed(() => PROVIDER_FLOW_CODES.includes(startError.value?.code))
// SyncWords refused the start (out of minutes, trial ended, ...): the account is the fix, not a reconnect.
const providerLinkText = computed(() => (startError.value?.code === 'captions_provider_rejected' ? 'Manage account' : 'Reconnect'))
const startErrorText = computed(() => {
	if (!startError.value) return ''
	if (FIELD_CODES.includes(startError.value.code)) return 'Some caption settings need fixing before captions can start. See Caption settings.'
	if ([502, 503, 504].includes(startError.value.status) && !startError.value.code) return 'The captioning service isn\'t reachable right now. Try again.'
	return startError.value.message
})

// ------------------------------------------------------------------ Ingest
const url = computed(() => props.flow.ingest?.url || '')
const passphrase = computed(() => props.flow.ingest?.passphrase || '')
const revealed = ref(false)

async function copy(value, what) {
	try {
		await navigator.clipboard.writeText(value)
		notify({ group: 'info', text: `${what} copied to clipboard` })
	} catch {
		notify({ group: 'error', text: `Could not copy the ${what.toLowerCase()}` })
	}
}

const stepBadge = (step) => {
	if (step === 1 && running.value && !isStarting.value) return 'bg-success text-white'
	if (step === 2 && isLive.value) return 'bg-success text-white'
	return activeStep.value === step ? 'bg-main text-white' : 'bg-surface-4 text-surface-8'
}
const stepDone = (step) => (step === 1 ? running.value && !isStarting.value : isLive.value)
</script>

<template>
	<section class="rounded-xl bg-surface-2 p-4 sm:p-5" aria-labelledby="sw-golive-heading" data-testid="syncwords-golive">
		<div class="mb-4 flex flex-wrap items-center gap-2">
			<h2 id="sw-golive-heading" class="m-0 text-base font-semibold text-surface-text-2">Go live with captions</h2>
			<span
				v-if="ownAccount"
				class="rounded-full bg-surface-3 px-2 py-0.5 text-[11px] font-medium text-surface-8 ring-1 ring-surface-4"
				:title="flow.syncwords?.providerAccount?.name ? `Runs on ${flow.syncwords.providerAccount.name}` : undefined"
				data-vendor-name
				data-testid="own-account-chip"
			>Your SyncWords account</span>
		</div>

		<ol class="m-0 grid list-none grid-cols-1 gap-4 p-0 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
			<!-- Step 1: start the service (the ingest only listens while it runs). -->
			<li
				class="flex min-w-0 flex-col gap-3 rounded-lg p-3 ring-1 transition-colors sm:p-4"
				:class="activeStep === 1 ? 'bg-surface-3/60 ring-main/50' : 'bg-surface-3/30 ring-white/5'"
				:data-active="activeStep === 1 ? 'true' : undefined"
				data-testid="golive-step-start"
			>
				<div class="flex flex-wrap items-center gap-2">
					<span class="flex size-6 shrink-0 items-center justify-center rounded-full text-tiny font-semibold" :class="stepBadge(1)" aria-hidden="true">
						<icon-base v-if="stepDone(1)" width="12" height="12" fill="none"><icon-checkmark /></icon-base>
						<template v-else>1</template>
					</span>
					<h3 class="m-0 text-sm font-semibold text-surface-text-2">Start captions</h3>
					<span
						class="ml-auto inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-tiny font-medium ring-1"
						:class="pillClass"
						:data-langs="langs.join(',')"
						:data-state="key"
						:title="headline.tooltip || undefined"
						data-testid="engine-state"
					>
						<ClipLoader v-if="isStarting" size="10px" class="shrink-0" />
						<span v-else class="size-1.5 shrink-0 rounded-full" :class="dotClass" aria-hidden="true" />
						<span>{{ pillText }}</span>
					</span>
				</div>
				<!-- Each language's Teletext page, as viewers' sets and players list it. -->
				<ul v-if="langs.length" class="m-0 flex list-none flex-wrap gap-1 p-0" aria-label="Caption languages" data-testid="golive-languages">
					<li
						v-for="(code, i) in langs"
						:key="code"
						class="rounded-full bg-surface-3 px-2 py-0.5 text-[11px] font-medium tabular-nums"
						:class="i === 0 ? 'text-surface-text-1' : 'text-surface-8'"
						:title="i === 0 ? 'Spoken language' : 'Translation'"
						:data-lang="code"
					>{{ langNames[i] }}<template v-if="teletextPage(flow, code)"> · page {{ teletextPage(flow, code) }}</template></li>
				</ul>

				<template v-if="!running">
					<p v-if="offCopy" class="m-0 text-xs text-surface-8" data-testid="off-copy">{{ offCopy }}</p>
					<div class="flex flex-col gap-2" data-testid="caption-duration">
						<div class="flex items-baseline justify-between gap-2">
							<span id="sw-duration-label" class="text-tiny font-medium text-surface-text-1">Duration</span>
							<span v-if="cycleLeftLabel" class="text-[11px] tabular-nums text-surface-7" data-testid="cycle-left">{{ cycleLeftLabel }}</span>
							<span v-else-if="usageLabel" class="text-right text-[11px] tabular-nums text-surface-7" data-vendor-name data-testid="provider-usage">{{ usageLabel }}</span>
							<span v-else-if="showBetaBadge" class="rounded-full bg-main-transparent px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-main-light">Free during beta</span>
						</div>
						<div v-if="durationPresets.length" class="flex flex-wrap gap-1.5" role="radiogroup" aria-labelledby="sw-duration-label">
							<button
								v-for="preset in durationPresets"
								:key="String(preset.value)"
								type="button"
								role="radio"
								:aria-checked="durationSec === preset.value"
								:aria-label="preset.label"
								:disabled="starting"
								class="inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-tiny font-medium ring-1 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
								:class="durationSec === preset.value
									? 'bg-main text-white ring-main'
									: 'bg-surface-3 text-surface-text-1 ring-white/5 hover:bg-surface-4 hover:text-white'"
								:data-duration="String(preset.value)"
								@click="durationSec = preset.value"
							>
								{{ preset.label }}
							</button>
						</div>
						<p class="m-0 text-xs text-surface-7" data-testid="duration-copy">{{ durationCopy }}</p>
					</div>
					<div class="mt-auto flex flex-col gap-2 sm:flex-row sm:items-center">
						<span
							class="flex sm:inline-flex"
							data-testid="start-wrapper"
							:data-blocked="startBlockedCode || undefined"
							@mouseenter="noteStartBlocked('hover')"
						>
							<Tooltip :active="!!startTooltip" :content="startTooltip" wrap-trigger wrapper-class="w-full" :hide="800">
								<FormButton ref="startButton" type="main" size="md" class="w-full justify-center" :disabled="!canStart" :is-loading="starting" @click="start">
									{{ ended ? 'Start again' : 'Start captions' }}
								</FormButton>
							</Tooltip>
						</span>
						<span v-if="pending?.dirty" class="text-xs text-gold" role="note" data-testid="unsaved-note">Your unsaved settings will be used.</span>
					</div>
				</template>

				<template v-else>
					<p v-if="isStarting" class="m-0 flex items-center gap-2 text-xs text-surface-8" data-testid="starting-copy">
						<ClipLoader size="12px" class="shrink-0" />
						Opening the ingest… this takes about a minute.
					</p>
					<p v-else-if="key === 'error'" class="m-0 text-xs text-error-light" data-testid="error-copy">
						The captioning service reported an error. Stop and start captions again; if it keeps happening, contact support.
					</p>
					<p v-else-if="key === 'unknown'" class="m-0 text-xs text-surface-8">Caption status is unavailable right now. Captions may still be running.</p>
					<div class="flex flex-wrap items-center gap-2" aria-live="off" data-testid="caption-window">
						<span class="text-tiny font-medium text-surface-text-1">
							Window <span class="text-surface-7">·</span> <span data-testid="window-duration">{{ durationLabel(win.durationSec) }}</span>
							<template v-if="!poolEndsFirst && !unlimited && endsAtLabel">
								<span class="text-surface-7"> · </span><span class="font-normal text-surface-8" data-testid="window-ends-at">ends {{ endsAtLabel }}</span>
							</template>
							<span v-else-if="!poolEndsFirst && unlimited" class="font-normal text-surface-8"> · runs until you stop it</span>
						</span>
						<span
							v-if="countdownLabel"
							class="ml-auto inline-flex h-7 items-center rounded-full bg-surface-3 px-2.5 text-tiny font-medium tabular-nums ring-1"
							:class="poolEndsFirst ? 'text-gold ring-gold/30' : 'text-surface-text-2 ring-surface-4'"
							data-testid="window-countdown"
						>{{ countdownLabel }}</span>
					</div>
					<p v-if="poolEndsFirst" class="m-0 text-xs text-gold">Your plan's hours run out before this window ends; captions stop then.</p>
					<div class="mt-auto flex flex-wrap items-center gap-2">
						<FormButton type="red" size="md" class="justify-center" :disabled="stopping" :is-loading="stopping" data-testid="stop-button" @click="emit('stop')">
							Stop captions
						</FormButton>
						<span v-if="cycleLeftLabel" class="ml-auto text-[11px] tabular-nums text-surface-7" data-testid="cycle-left">{{ cycleLeftLabel }}</span>
						<span v-else-if="usageLabel" class="ml-auto text-[11px] tabular-nums text-surface-7" data-vendor-name data-testid="provider-usage">{{ usageLabel }}</span>
					</div>
				</template>

				<p v-if="error" class="m-0 text-xs text-error-light" role="alert">{{ error }}</p>
				<Alert v-if="startErrorText" type="error" role="alert" :data-error-code="startError.code || undefined" :data-vendor-name="providerError ? '' : undefined" data-testid="start-error">
					<span class="flex w-full flex-wrap items-center gap-x-1 gap-y-1">
						<span>{{ startErrorText }}</span>
						<router-link v-if="providerError" :to="PROVIDER_ROUTE" class="font-medium text-white underline" data-testid="start-error-provider-link">{{ providerLinkText }}</router-link>
						<router-link v-if="startError.upgrade" :to="UPGRADE_ROUTE" class="font-medium text-white underline" @click="track(EVENTS.UPGRADE_CLICKED, { source: 'captioning-limit' })">Upgrade</router-link>
					</span>
				</Alert>
			</li>

			<!-- Step 2: the encoder target; it accepts the encoder once step 1 runs. -->
			<li
				class="flex min-w-0 flex-col gap-3 rounded-lg p-3 ring-1 transition-colors sm:p-4"
				:class="[
					activeStep === 2 && isLive ? 'bg-surface-3/60 ring-success/40' : activeStep === 2 ? 'bg-surface-3/60 ring-main/50' : 'bg-surface-3/30 ring-white/5',
				]"
				:data-active="activeStep === 2 ? 'true' : undefined"
				:data-state="key"
				data-testid="golive-step-encoder"
			>
				<div class="flex flex-wrap items-center gap-2">
					<span class="flex size-6 shrink-0 items-center justify-center rounded-full text-tiny font-semibold" :class="stepBadge(2)" aria-hidden="true">
						<icon-base v-if="stepDone(2)" width="12" height="12" fill="none"><icon-checkmark /></icon-base>
						<template v-else>2</template>
					</span>
					<h3 class="m-0 text-sm font-semibold text-surface-text-2">Send your encoder here</h3>
					<span class="rounded bg-surface-4 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-surface-8">SRT</span>
					<router-link
						v-if="streamRoute"
						:to="streamRoute"
						class="ml-auto inline-flex items-center gap-1 text-tiny font-medium text-main-light hover:underline"
						data-testid="linked-stream-link"
					>
						Open {{ flow.streamName || 'stream' }}
						<icon-base width="12" height="12" width-view-box="24" height-view-box="24" fill="none"><icon-arrow-right /></icon-base>
					</router-link>
				</div>

				<p v-if="isWaiting" class="m-0 text-tiny font-medium text-gold" data-testid="encoder-hint" data-hint="ready">
					Ready: start your encoder now.
					<span v-if="autoStop" class="font-normal" data-testid="auto-stop">{{ autoStop }}</span>
				</p>
				<p v-else-if="isLive" class="m-0 text-tiny text-success" data-testid="encoder-hint" data-hint="live">
					<span class="mr-1.5 inline-block size-1.5 rounded-full bg-success align-middle" aria-hidden="true" />
					<span data-testid="input-indicator">Encoder connected</span>
					<span class="text-surface-8">· captioned video is flowing into {{ streamName }}.</span>
				</p>
				<p v-else-if="isStarting" class="m-0 text-xs text-surface-8" data-testid="encoder-hint" data-hint="opening">
					Opening in a moment. Start your encoder when this step says Ready.
				</p>
				<p v-else-if="!running" class="m-0 text-xs text-surface-8" data-testid="encoder-hint" data-hint="off">
					Accepts your encoder once captions are started.
				</p>
				<p v-else-if="inputConnected === false" class="m-0 text-xs text-gold" data-testid="encoder-hint" data-hint="no-input">
					<span data-testid="input-indicator">No encoder input</span>
				</p>

				<div class="grid grid-cols-1 gap-3 transition-opacity" :class="activeStep === 1 ? 'opacity-70' : ''">
					<div class="flex min-w-0 flex-col gap-1.5">
						<span id="sw-ingest-url-label" class="text-xs text-surface-8">SRT ingest URL</span>
						<div v-if="url" class="flex min-w-0 items-center gap-2">
							<code
								class="flex h-8 min-w-0 flex-1 items-center overflow-x-auto whitespace-nowrap rounded bg-surface-3 px-3 font-mono text-tiny text-surface-text-2"
								aria-labelledby="sw-ingest-url-label"
								data-testid="ingest-url"
							>{{ url }}</code>
							<FormButton type="secondary" size="md" class="shrink-0 gap-1.5" aria-label="Copy ingest URL" data-testid="copy-ingest-url" @click="copy(url, 'Ingest URL')">
								<icon-base width="14" height="14" fill="none"><icon-copy /></icon-base>
								<span class="hidden sm:inline">Copy</span>
							</FormButton>
						</div>
						<p v-else class="m-0 flex h-8 items-center text-tiny text-surface-7" data-testid="ingest-url-pending">The ingest URL is being prepared. Reload in a moment.</p>
					</div>

					<div v-if="passphrase" class="flex min-w-0 flex-col gap-1.5">
						<span id="sw-ingest-pass-label" class="text-xs text-surface-8">Passphrase</span>
						<div class="flex min-w-0 items-center gap-2">
							<code
								class="flex h-8 min-w-0 flex-1 items-center overflow-x-auto whitespace-nowrap rounded bg-surface-3 px-3 font-mono text-tiny text-surface-text-2"
								aria-labelledby="sw-ingest-pass-label"
								data-testid="ingest-passphrase"
							>{{ revealed ? passphrase : '•'.repeat(Math.min(passphrase.length, 16)) }}</code>
							<FormButton type="secondary" size="md" is-icon class="shrink-0" :aria-label="revealed ? 'Hide passphrase' : 'Show passphrase'" @click="revealed = !revealed">
								<icon-base width="16" height="16" fill="none">
									<icon-eye-crossed v-if="revealed" />
									<icon-eye v-else />
								</icon-base>
							</FormButton>
							<FormButton type="secondary" size="md" is-icon class="shrink-0" aria-label="Copy passphrase" data-testid="copy-passphrase" @click="copy(passphrase, 'Passphrase')">
								<icon-base width="14" height="14" fill="none"><icon-copy /></icon-base>
							</FormButton>
						</div>
					</div>
				</div>

				<p class="m-0 text-xs text-surface-7" data-testid="ingest-explainer">
					Your encoder sends to this captioning ingest, and the captioned video arrives in <span class="font-medium text-surface-text-1">{{ streamName }}</span>, its player and restream destinations.
				</p>
				<!-- The stream's "Enable Caption Support" player setting, which creating the flow turned on. -->
				<p v-if="captionSupport?.enabled" class="m-0 text-xs text-surface-7" data-testid="caption-support" data-enabled="true">
					Caption support is on for {{ streamName }}'s player.
				</p>
				<p v-else-if="captionSupport" class="m-0 text-xs text-gold" data-testid="caption-support" data-enabled="false">
					Caption support is off for {{ streamName }}'s player, so captions won't show there.
					<router-link v-if="playerSettingsRoute" :to="playerSettingsRoute" class="font-medium text-main-light hover:underline">Turn it on in Player settings</router-link>
				</p>
			</li>
		</ol>
	</section>
</template>
